/**
 * Hook that loads and persists Status Overview bar-visibility
 * preferences via the Loom User Documents API.
 *
 * On first render: reads from Loom Documents (falls back to
 * DEFAULT_VISIBILITY). On toggle: debounces and writes back.
 * If the Documents API is unavailable (local dev, prod where
 * FEATURE_DISABLED), the hook degrades to pure in-memory state.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { BarKey } from "./chartData";
import { DEFAULT_VISIBILITY } from "./StatusOverview";
import {
  createDocument,
  DACW_SERVICE_KEY,
  getDocument,
  getErrorCode,
  isUserDocumentsAvailable,
  listMyDocuments,
  NS_STATUS_OVERVIEW_PREFS,
  updateDocument,
  UserDocument,
} from "../../app/userDocumentsClient";

export type BarVisibility = Record<BarKey, boolean>;

/** Schema version so we can migrate the body shape in the future. */
const SCHEMA_VERSION = 1;

/** Debounce delay in ms before persisting a change. */
const SAVE_DEBOUNCE_MS = 800;

interface PrefsDocBody {
  version: number;
  visibility: BarVisibility;
}

function parseBody(raw: string | null | undefined): BarVisibility | null {
  if (!raw) return null;
  try {
    // The Documents API returns body double-stringified (JSON string
    // wrapped in quotes). Parse until we get an object.
    let parsed: unknown = JSON.parse(raw);
    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }
    const body = parsed as PrefsDocBody;
    if (body.version !== SCHEMA_VERSION) return null;
    // Validate that every expected key is present.
    const vis = body.visibility;
    if (typeof vis !== "object" || vis === null) return null;
    const keys: BarKey[] = Object.keys(DEFAULT_VISIBILITY) as BarKey[];
    for (const key of keys) {
      if (typeof vis[key] !== "boolean") {
        // Fill in missing keys with defaults (forward-compat).
        vis[key] = DEFAULT_VISIBILITY[key];
      }
    }
    return vis;
  } catch {
    return null;
  }
}

function serializeBody(visibility: BarVisibility): string {
  const body: PrefsDocBody = { version: SCHEMA_VERSION, visibility };
  return JSON.stringify(body);
}

export interface UseStatusOverviewPrefsReturn {
  /** Current visibility state (reactive). */
  visibleBars: BarVisibility;
  /** Toggle a single bar key. Triggers debounced persistence. */
  toggleBar: (key: BarKey) => void;
  /** True while the initial load from Loom is in-flight. */
  loading: boolean;
}

export function useStatusOverviewPrefs(): UseStatusOverviewPrefsReturn {
  const [visibleBars, setVisibleBars] =
    useState<BarVisibility>(DEFAULT_VISIBILITY);
  const [loading, setLoading] = useState(true);

  // Ref to track the current document ID + version for updates.
  const docRef = useRef<{ docId: string; bodyVersion: number } | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track latest visibility so the save closure reads fresh state.
  const latestVisibilityRef = useRef<BarVisibility>(DEFAULT_VISIBILITY);
  // Track whether persistence is disabled (FEATURE_DISABLED in prod).
  const disabledRef = useRef(false);
  // Guard: prevent persist() from creating a duplicate document before
  // the initial load completes.
  const loadedRef = useRef(false);

  // ---- Initial load ----
  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!isUserDocumentsAvailable()) {
        setLoading(false);
        return;
      }
      try {
        const docs = await listMyDocuments(NS_STATUS_OVERVIEW_PREFS, 1);
        if (cancelled) return;
        if (docs.length > 0) {
          // list() doesn't return body, so fetch the full document.
          const doc = await getDocument(docs[0].docId);
          if (cancelled) return;
          if (doc) {
            const parsed = parseBody(doc.body);
            if (parsed) {
              setVisibleBars(parsed);
              latestVisibilityRef.current = parsed;
            }
            docRef.current = {
              docId: doc.docId,
              bodyVersion: doc.bodyVersion,
            };
          }
        }
      } catch (e) {
        const code = getErrorCode(e);
        if (code === "FEATURE_DISABLED") {
          // Prod environment — silently disable persistence.
          disabledRef.current = true;
        }
        console.debug(
          "[useStatusOverviewPrefs] Failed to load preferences:",
          e
        );
      } finally {
        if (!cancelled) {
          loadedRef.current = true;
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Debounced persist ----
  const persist = useCallback(() => {
    if (
      !loadedRef.current ||
      !isUserDocumentsAvailable() ||
      disabledRef.current
    )
      return;

    const body = serializeBody(latestVisibilityRef.current);

    if (docRef.current) {
      // Update existing document.
      updateDocument({
        docId: docRef.current.docId,
        body,
        expectedVersion: docRef.current.bodyVersion,
      })
        .then((updated) => {
          docRef.current = {
            docId: updated.docId,
            bodyVersion: updated.bodyVersion,
          };
        })
        .catch(async (e) => {
          const code = getErrorCode(e);
          if (code === "CONFLICT") {
            // Another tab/session updated the doc. Re-read and retry.
            try {
              const fresh = await getDocument(docRef.current!.docId);
              if (fresh) {
                docRef.current = {
                  docId: fresh.docId,
                  bodyVersion: fresh.bodyVersion,
                };
                // Retry the save with the fresh version.
                const retryBody = serializeBody(latestVisibilityRef.current);
                const retried = await updateDocument({
                  docId: fresh.docId,
                  body: retryBody,
                  expectedVersion: fresh.bodyVersion,
                });
                docRef.current = {
                  docId: retried.docId,
                  bodyVersion: retried.bodyVersion,
                };
              }
            } catch (retryErr) {
              console.debug(
                "[useStatusOverviewPrefs] Conflict retry failed:",
                retryErr
              );
            }
          } else if (code === "FEATURE_DISABLED") {
            disabledRef.current = true;
          } else {
            console.debug(
              "[useStatusOverviewPrefs] Failed to update preferences:",
              e
            );
          }
        });
    } else {
      // Create new document.
      createDocument({
        serviceKey: DACW_SERVICE_KEY,
        namespace: NS_STATUS_OVERVIEW_PREFS,
        body,
        title: "Status Overview Preferences",
        visibility: "PRIVATE",
        schemaVersion: SCHEMA_VERSION,
      })
        .then((created) => {
          docRef.current = {
            docId: created.docId,
            bodyVersion: created.bodyVersion,
          };
        })
        .catch((e) => {
          const code = getErrorCode(e);
          if (code === "FEATURE_DISABLED") {
            disabledRef.current = true;
          } else {
            console.debug(
              "[useStatusOverviewPrefs] Failed to create preferences:",
              e
            );
          }
        });
    }
  }, []);

  const toggleBar = useCallback(
    (key: BarKey) => {
      setVisibleBars((prev) => {
        const next = { ...prev, [key]: !prev[key] };
        latestVisibilityRef.current = next;
        return next;
      });
      // Debounce: cancel any pending save and schedule a new one.
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        persist();
      }, SAVE_DEBOUNCE_MS);
    },
    [persist]
  );

  // Cleanup timer on unmount — flush pending save so preferences
  // aren't lost if the user navigates away within the debounce window.
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
        persist();
      }
    };
  }, [persist]);

  return { visibleBars, toggleBar, loading };
}
